/**
 * Aircraft (Settings → Aircraft; help `cloud.aircraft`): the organisation's aircraft register (MB's R41, decision
 * 506). Every staff member reads it; tenant admins and managers set each aircraft's register settings: its type (C208,
 * C210 or helicopter, which also picks the Geosoft script set), engines, whether engine starts and oil uplift are
 * recorded, the oil dipstick's unit (R150, decision 920), fuel type and unit, tracker devices, MR hours, maximum
 * endurance and nominal fuel burn. They sync to Teslyn Field and shape each flight entry there. The tracker devices to link are those the organisation's tracking
 * accounts have reported and its tracker assets (docs/specs/cloud/aircraft-trackers.md); an aircraft may carry
 * several. Tenant admins and managers also import aircraft from TJR files (MB's R79), and set each aircraft's own
 * photo (decision 598: from a project photo or uploaded, AircraftPhotoField), which the daily and weekly reports in
 * Teslyn Field show. Below the register, the company's Geosoft templates per aircraft type (decision 889,
 * GeosoftTemplatesPanel), copied to every Teslyn Field laptop.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  AIRFRAME_LABEL,
  DIPSTICK_UNITS,
  DIPSTICK_UNIT_LABEL,
  FLIGHT_WATCH_HINT,
  aircraftAirframe,
  aircraftEngines,
  aircraftOpenSettingsAccess,
  aircraftPhotoWords,
  canSetAircraftPhoto,
  isCloudAircraftPhoto,
  aircraftRegisterAccess,
  aircraftSettingsWords,
  engineStartsTracked,
  flightWatchHint,
  linkedTrackers,
  matchingTrackerLinks,
  trackerChoices,
  trackerOptionsFor,
  validateAircraftSettings,
  type Aircraft,
  type Airframe,
  type DipstickUnit,
  type FuelType,
  type FuelUnit,
  type SeenDevice,
  type TrackerAsset,
  type TrackerChoice,
  type ValidationIssue,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { CheckField, IssueList, SelectField, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import {
  linkTracker,
  loadAircraft,
  loadFlightWatchSeen,
  loadSeenDevices,
  loadTrackers,
  saveAircraftSettings,
  unlinkTracker,
} from './api.ts';
import { AircraftImportPanel } from './AircraftImportPanel.tsx';
import { AircraftPhotoField } from './AircraftPhotoField.tsx';
import { AltimeterCalsField } from './AltimeterCalsField.tsx';
import { GeosoftTemplatesPanel } from './GeosoftTemplatesPanel.tsx';
import { aircraftPhotoUrls } from './photoApi.ts';
import { browserPhotoRenderer, type PhotoRenderer } from '../portal/photoImages.ts';
import { TableWrap } from '../components/TableWrap.tsx';

/** What the tracker field is for, on the page and in the help (MB, 25 Oct 2026). */
export const TRACKER_FIELD_HINT =
  'Links the aircraft to its tracker, so live positions show as this aircraft on the maps and in the Hub.';

export interface AircraftScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  /** The organisation's name, to see which registered aircraft are its own (the TJR import). */
  readonly tenantName?: string;
  /** The viewer's tenant roles (display only; the database decides). */
  readonly roles: readonly string[];
  /** The time the "seen … ago" words are measured from (tests); now by default. */
  readonly now?: () => number;
  /** Draws an uploaded aircraft photo at the display size (tests inject one: jsdom has no canvas). */
  readonly renderer?: PhotoRenderer;
  /** The file id of a new aircraft photo (tests); a random UUID by default. */
  readonly newPhotoId?: () => string;
}

interface Draft {
  readonly airframe: Airframe | '';
  readonly engines: '' | '1' | '2';
  readonly starts: boolean;
  readonly oil: boolean;
  /** The unit the oil dipstick reads in (decision 920); '' not set. */
  readonly dipstick: DipstickUnit | '';
  readonly fuelType: FuelType | '';
  readonly fuelUnit: FuelUnit;
  readonly mr: string;
  readonly endurance: string;
  readonly burn: string;
  /** The JSA's figures kept in the register (decision 863, 6 B). */
  readonly emptyKg: string;
  readonly mtowKg: string;
  readonly reserveMin: string;
  /** The type in words ("Cessna 210N"); empty for an aircraft imported with its type unknown (decision 547). */
  readonly typeName: string;
  /** The device keys linked to the aircraft (several allowed: V2Track and TracPlus on VH-JEI). */
  readonly trackers: readonly string[];
  /** "Uses V2Track flight watch" (R184): only with a V2Track tracker. */
  readonly flightWatch: boolean;
}

/** What the flight watch tick does, on the page and in the help (R184). */
export const FLIGHT_WATCH_FIELD_HINT =
  'Alerts when the aircraft is airborne without V2Track’s Manual Flight Watch started, and when it has landed with it still on. Only for an aircraft with a V2Track tracker.';

const text = (v: number | null | undefined): string => (v == null ? '' : String(v));

function draftOf(a: Aircraft, choices: readonly TrackerChoice[]): Draft {
  const engines = aircraftEngines(a);
  return {
    airframe: a.airframe ?? '',
    engines: engines === null ? '' : engines === 1 ? '1' : '2',
    starts: engineStartsTracked(a),
    oil: a.oil_uplift_tracked !== false,
    dipstick: a.oil_dipstick_unit ?? '',
    fuelType: a.fuel_type ?? '',
    fuelUnit: a.fuel_unit ?? 'L',
    mr: text(a.mr_interval_h),
    endurance: text(a.max_flight_h),
    burn: text(a.fuel_flow_lph),
    emptyKg: text(a.empty_weight_kg),
    mtowKg: text(a.mtow_kg),
    reserveMin: text(a.reserve_min),
    typeName: a.type_name ?? '',
    trackers: linkedTrackers(choices, a.id).map((c) => c.key),
    flightWatch: a.uses_flight_watch === true,
  };
}

export function AircraftScreen({
  sb,
  tenantId,
  tenantName = '',
  roles,
  now = Date.now,
  renderer = browserPhotoRenderer,
  newPhotoId,
}: AircraftScreenProps): ReactElement {
  // decision 506: the five batch 3 settings, the tracker devices and the TJR import for tenant admins and managers;
  // decision 537 point 4 B: fuel type, MR hours, endurance and fuel burn for every staff member
  const full = aircraftRegisterAccess(roles, false) === 'edit';
  const editable = full || aircraftOpenSettingsAccess(roles, false) === 'edit';
  // decision 598: tenant admins and managers set each aircraft's photo
  const photoSetter = canSetAircraftPhoto(roles);
  const [aircraft, setAircraft] = useState<Aircraft[] | null>(null);
  const [trackers, setTrackers] = useState<TrackerAsset[]>([]);
  const [seen, setSeen] = useState<SeenDevice[]>([]);
  // R184: the tracker devices the cloud saw log flight watch (the register's hint)
  const [watchSeen, setWatchSeen] = useState<ReadonlySet<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Aircraft | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [issues, setIssues] = useState<readonly ValidationIssue[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [photoUrls, setPhotoUrls] = useState<ReadonlyMap<string, string>>(new Map());

  const load = useCallback(() => {
    setError(null);
    setAircraft(null);
    Promise.all([
      loadAircraft(sb, tenantId),
      loadTrackers(sb, tenantId).catch(() => []),
      loadSeenDevices(sb, tenantId).catch(() => []),
      loadFlightWatchSeen(sb, tenantId).catch(() => new Set<string>()),
    ])
      .then(([a, t, d, w]) => {
        setAircraft(a);
        setTrackers(t);
        setSeen(d);
        setWatchSeen(w);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [sb, tenantId]);
  useEffect(load, [load]);

  // the register's thumbnails of the aircraft's own photos
  const photoKey = (aircraft ?? [])
    .map((a) => a.cloud_photo_path)
    .filter((p): p is string => isCloudAircraftPhoto(p))
    .join('|');
  useEffect(() => {
    let live = true;
    const objects = photoKey === '' ? [] : photoKey.split('|');
    void aircraftPhotoUrls(sb, objects).then((m) => live && setPhotoUrls(m));
    return () => {
      live = false;
    };
  }, [sb, photoKey]);

  const choices = trackerChoices(seen, trackers, now());
  const matching = matchingTrackerLinks(choices, aircraft ?? []);

  async function applyLinks(target: Aircraft, keys: readonly string[]): Promise<void> {
    for (const c of linkedTrackers(choices, target.id))
      if (!keys.includes(c.key) && c.asset) await unlinkTracker(sb, c.asset.id);
    for (const key of keys) {
      const c = choices.find((x) => x.key === key);
      if (c && c.asset?.aircraft_id !== target.id) await linkTracker(sb, tenantId, target, c);
    }
  }

  async function linkAllMatching(): Promise<void> {
    setBusy(true);
    setMessage(null);
    try {
      for (const l of matching) {
        const a = (aircraft ?? []).find((x) => x.id === l.aircraftId);
        if (a) await linkTracker(sb, tenantId, a, l.choice);
      }
      setTrackers(await loadTrackers(sb, tenantId));
      const words = matching.map((l) => `${l.registration} (${l.choice.text.split(' · ')[1]})`).join(', ');
      setMessage(
        `Linked ${matching.length} tracker ${matching.length === 1 ? 'device' : 'devices'}: ${words}.`,
      );
    } catch (e) {
      setMessage(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const draftHasV2Track = (draft?.trackers ?? []).some((k) => k.startsWith('v2track|'));

  async function submit(): Promise<void> {
    if (!editing || !draft) return;
    const num = (s: string, field: string, word: string, out: ValidationIssue[]): number | null => {
      if (s.trim() === '') return null;
      const n = Number(s.trim().replace(',', '.'));
      if (!Number.isFinite(n)) out.push({ field, message: `${word} must be a number.` });
      return Number.isFinite(n) ? n : null;
    };
    const found: ValidationIssue[] = [];
    const candidate: Aircraft = {
      ...editing,
      airframe: draft.airframe === '' ? null : draft.airframe,
      engines: draft.engines === '' ? null : draft.engines === '1' ? 1 : 2,
      engine_starts_tracked: draft.starts,
      oil_uplift_tracked: draft.oil,
      oil_dipstick_unit: draft.dipstick === '' ? null : draft.dipstick,
      // R184: only with a V2Track tracker among the devices chosen; untick when the last one is removed
      uses_flight_watch:
        draft.flightWatch && draftHasV2Track ? true : editing.uses_flight_watch == null ? null : false,
      fuel_type: draft.fuelType === '' ? null : draft.fuelType,
      fuel_unit: draft.fuelUnit,
      mr_interval_h: num(draft.mr, 'mr_interval_h', 'MR hours', found),
      max_flight_h: num(draft.endurance, 'max_flight_h', 'Maximum endurance', found),
      fuel_flow_lph: num(draft.burn, 'fuel_flow_lph', 'Nominal fuel burn', found),
      empty_weight_kg: num(draft.emptyKg, 'empty_weight_kg', 'Empty weight', found),
      mtow_kg: num(draft.mtowKg, 'mtow_kg', 'MTOW', found),
      reserve_min: num(draft.reserveMin, 'reserve_min', 'Reserve', found),
      type_name: draft.typeName.trim(),
    };
    found.push(...validateAircraftSettings(candidate));
    setIssues(found);
    if (found.length > 0) return;
    setBusy(true);
    try {
      const saved = await saveAircraftSettings(sb, candidate, full ? 'all' : 'open');
      const before = linkedTrackers(choices, editing.id).map((c) => c.key);
      if (full && before.join('|') !== draft.trackers.join('|')) {
        await applyLinks(saved, draft.trackers);
        setTrackers(await loadTrackers(sb, tenantId));
      }
      setAircraft((list) => (list ?? []).map((a) => (a.id === saved.id ? saved : a)));
      setMessage(`Saved ${saved.registration}.`);
      setEditing(null);
    } catch (e) {
      setIssues([{ field: 'save', message: e instanceof Error ? e.message : String(e) }]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="page">
      <PageHeader
        title="Aircraft"
        subtitle="The aircraft register: each aircraft's settings shape its flight entries in Teslyn Field."
      >
        {full && !editing && !importing && (
          <Button
            onClick={() => {
              setImporting(true);
              setMessage(null);
            }}
          >
            Import aircraft
          </Button>
        )}
      </PageHeader>
      {!editable && (
        <p className="body muted">
          Staff change the aircraft register. Aircraft are added in Teslyn Field&apos;s Project settings.
        </p>
      )}
      {importing && aircraft && (
        <AircraftImportPanel
          sb={sb}
          tenantId={tenantId}
          tenantName={tenantName}
          aircraft={aircraft}
          onCancel={() => setImporting(false)}
          onDone={(m) => {
            setImporting(false);
            load();
            setMessage(m);
          }}
        />
      )}
      {full && !editing && matching.length > 0 && (
        <div className="panel row">
          <p className="body">
            {matching.length === 1
              ? 'One tracker device reports the registration of an aircraft but is not linked to it'
              : `${matching.length} tracker devices report the registration of an aircraft but are not linked to it`}
            : {matching.map((l) => l.registration).join(', ')}.
          </p>
          <Button variant="primary" busy={busy} onClick={() => void linkAllMatching()}>
            Link all matching trackers
          </Button>
        </div>
      )}
      {message && (
        <p className="body" role="status">
          {message}
        </p>
      )}
      {editing && draft && (
        <form
          className="panel stack"
          aria-label={`Edit ${editing.registration}`}
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <h2 className="heading">Edit {editing.registration}</h2>
          <IssueList issues={issues} title="Not saved" />
          {!full && (
            <p className="caption muted">
              Tenant admins and managers change the aircraft type, engines, engine starts, oil uplift,
              dipstick unit, fuel unit and tracker device; you change the fuel type, MR hours, endurance, fuel
              burn and the JSA&apos;s figures.
            </p>
          )}
          <div className="form-grid">
            <SelectField<Airframe | ''>
              disabled={!full}
              label="Aircraft type"
              hint="Also picks the Geosoft script set."
              value={draft.airframe}
              onChange={(v) => setDraft({ ...draft, airframe: v })}
              options={[
                ['', 'From the type name'],
                ['c208', AIRFRAME_LABEL.c208],
                ['c210', AIRFRAME_LABEL.c210],
                ['heli', AIRFRAME_LABEL.heli],
              ]}
            />
            <SelectField<'' | '1' | '2'>
              disabled={!full}
              label="Engines"
              value={draft.engines}
              onChange={(v) => setDraft({ ...draft, engines: v })}
              options={[
                ['', '–'],
                ['1', '1 (single)'],
                ['2', '2 (twin)'],
              ]}
            />
            <SelectField<FuelType | ''>
              label="Fuel type"
              value={draft.fuelType}
              onChange={(v) => setDraft({ ...draft, fuelType: v })}
              options={[
                ['', '–'],
                ['avgas', 'Avgas'],
                ['turbine', 'Jet A-1 (turbine)'],
              ]}
            />
            <SelectField<FuelUnit>
              disabled={!full}
              label="Fuel unit"
              value={draft.fuelUnit}
              onChange={(v) => setDraft({ ...draft, fuelUnit: v })}
              options={[
                ['L', 'Litres'],
                ['USG', 'US gallons'],
              ]}
            />
            <TextField
              label="MR hours (h)"
              spellCheck={false}
              value={draft.mr}
              onChange={(v) => setDraft({ ...draft, mr: v })}
            />
            <TextField
              label="Maximum endurance (h)"
              spellCheck={false}
              hint="A planned or entered flight longer than this is warned about."
              value={draft.endurance}
              onChange={(v) => setDraft({ ...draft, endurance: v })}
            />
            <TextField
              label="Nominal fuel burn (L/h)"
              spellCheck={false}
              value={draft.burn}
              onChange={(v) => setDraft({ ...draft, burn: v })}
            />
            <TextField
              label="Type name"
              hint="The make and model in words, for example Cessna 210N. Blank shows Type unknown."
              value={draft.typeName}
              onChange={(v) => setDraft({ ...draft, typeName: v })}
            />
          </div>
          <fieldset className="fieldset stack">
            <legend className="label">For the JSA&apos;s performance planning</legend>
            <p className="caption muted">
              Filled into each JSA in Teslyn Field (A, F and H), where a JSA can still change them.
            </p>
            <div className="form-grid">
              <TextField
                label="Empty weight in survey configuration (kg)"
                spellCheck={false}
                value={draft.emptyKg}
                onChange={(v) => setDraft({ ...draft, emptyKg: v })}
              />
              <TextField
                label="MTOW (kg)"
                spellCheck={false}
                value={draft.mtowKg}
                onChange={(v) => setDraft({ ...draft, mtowKg: v })}
              />
              <TextField
                label="Reserve (min)"
                spellCheck={false}
                hint="The reserve fuel the JSA keeps, in minutes of flight."
                value={draft.reserveMin}
                onChange={(v) => setDraft({ ...draft, reserveMin: v })}
              />
            </div>
          </fieldset>
          <TrackerDevicesField
            disabled={!full}
            aircraft={editing}
            all={aircraft ?? []}
            choices={choices}
            keys={draft.trackers}
            onChange={(keys) => setDraft({ ...draft, trackers: keys })}
          />
          <CheckField
            disabled={!full || !draftHasV2Track}
            label="Uses V2Track flight watch"
            checked={draft.flightWatch && draftHasV2Track}
            onChange={(v) => setDraft({ ...draft, flightWatch: v })}
          />
          <p className="caption muted">
            {draftHasV2Track
              ? FLIGHT_WATCH_FIELD_HINT
              : 'Add the aircraft’s V2Track tracker device first: flight watch is V2Track’s.'}
          </p>
          {!draft.flightWatch && flightWatchHint(editing, trackers, watchSeen) && (
            <p className="caption">{FLIGHT_WATCH_HINT}.</p>
          )}
          <CheckField
            disabled={!full}
            label="Record engine starts (off for piston engines)"
            checked={draft.starts}
            onChange={(v) => setDraft({ ...draft, starts: v })}
          />
          <CheckField
            disabled={!full}
            label="Record oil uplift, per engine"
            checked={draft.oil}
            onChange={(v) => setDraft({ ...draft, oil: v })}
          />
          <SelectField<DipstickUnit | ''>
            disabled={!full}
            label="Oil dipstick unit"
            hint="What the flight entry's Dipstick before oil boxes, beside the oil boxes, read in."
            value={draft.dipstick}
            onChange={(v) => setDraft({ ...draft, dipstick: v })}
            options={[['', 'Not set'], ...DIPSTICK_UNITS.map((u) => [u, DIPSTICK_UNIT_LABEL[u]] as const)]}
          />
          <AircraftPhotoField
            sb={sb}
            aircraft={editing}
            mayChange={photoSetter}
            renderer={renderer}
            newId={newPhotoId}
            onChanged={(saved) => {
              setEditing({ ...editing, cloud_photo_path: saved.cloud_photo_path ?? null });
              setAircraft((list) =>
                (list ?? []).map((a) =>
                  a.id === saved.id ? { ...a, cloud_photo_path: saved.cloud_photo_path ?? null } : a,
                ),
              );
            }}
          />
          <AltimeterCalsField
            sb={sb}
            aircraft={editing}
            roles={roles}
            onChanged={(cals) => {
              setEditing({ ...editing, altimeter_cals: cals });
              setAircraft((list) =>
                (list ?? []).map((a) => (a.id === editing.id ? { ...a, altimeter_cals: cals } : a)),
              );
            }}
          />
          <div className="row">
            <Button variant="primary" type="submit" busy={busy}>
              Save
            </Button>
            <Button variant="quiet" onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
      {error ? (
        <ErrorState message={error} onRetry={load} />
      ) : aircraft === null ? (
        <Loading what="aircraft" />
      ) : aircraft.length === 0 ? (
        <p className="body muted">No aircraft yet. They are added in Teslyn Field&apos;s Project settings.</p>
      ) : (
        // a phone scrolls the register sideways, never the page
        <TableWrap>
          <table className="table" aria-label="Aircraft register">
            <thead>
              <tr>
                <th>Registration</th>
                <th>Photo</th>
                <th>Type</th>
                <th>Settings</th>
                <th>
                  <span title={TRACKER_FIELD_HINT}>Tracker devices</span>
                </th>
                {editable && (
                  <th>
                    <span className="sr-only">Action</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {aircraft.map((a) => {
                const type = aircraftAirframe(a);
                return (
                  <tr key={a.id}>
                    <td className="data">{a.registration}</td>
                    <td>
                      {photoUrls.get(a.cloud_photo_path ?? '') ? (
                        <img
                          className="aircraft-photo__thumb"
                          src={photoUrls.get(a.cloud_photo_path ?? '')}
                          alt={`Photo of ${a.registration}`}
                        />
                      ) : (
                        <span className="caption muted">{aircraftPhotoWords(a)}</span>
                      )}
                    </td>
                    <td>{type ? AIRFRAME_LABEL[type] : a.type_name || 'Type unknown'}</td>
                    <td className="caption">
                      {aircraftSettingsWords(a)}
                      {flightWatchHint(a, trackers, watchSeen) && (
                        <>
                          <br />
                          <span>{FLIGHT_WATCH_HINT}.</span>
                        </>
                      )}
                    </td>
                    <td>
                      {linkedTrackers(choices, a.id)
                        .map((c) => c.text.split(' · ').slice(0, 2).join(' · '))
                        .join('; ') || 'None'}
                    </td>
                    {editable && (
                      <td>
                        <Button
                          variant="quiet"
                          aria-label={`Edit ${a.registration}`}
                          onClick={() => {
                            setEditing(a);
                            setDraft(draftOf(a, choices));
                            setIssues([]);
                            setMessage(null);
                          }}
                        >
                          Edit
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      )}
      {/* decision 889: the company's template Geosoft folder per aircraft type, copied to every laptop */}
      {!editing && !importing && <GeosoftTemplatesPanel sb={sb} tenantId={tenantId} roles={roles} />}
    </section>
  );
}

/**
 * The aircraft's tracker devices: those linked, each with Remove, and a list to add one from, the devices whose
 * registration is the aircraft's first ("Matches the registration"); a device linked to another aircraft says so and
 * moves when chosen.
 */
function TrackerDevicesField({
  disabled,
  aircraft,
  all,
  choices,
  keys,
  onChange,
}: {
  /** Read only: the tracker devices are for tenant admins and managers (decision 506). */
  readonly disabled: boolean;
  readonly aircraft: Aircraft;
  readonly all: readonly Aircraft[];
  readonly choices: readonly TrackerChoice[];
  readonly keys: readonly string[];
  readonly onChange: (keys: readonly string[]) => void;
}): ReactElement {
  const chosen = keys
    .map((k) => choices.find((c) => c.key === k))
    .filter((c): c is TrackerChoice => Boolean(c));
  const options = trackerOptionsFor(choices, aircraft, all).filter((o) => !keys.includes(o.choice.key));
  return (
    <fieldset className="fieldset stack">
      <legend className="label">Tracker devices</legend>
      <p className="caption muted">{TRACKER_FIELD_HINT}</p>
      {chosen.length === 0 ? (
        <p className="body">None linked.</p>
      ) : (
        <ul className="stack" aria-label={`Tracker devices of ${aircraft.registration}`}>
          {chosen.map((c) => (
            <li key={c.key} className="row">
              <span className="body">{c.text}</span>
              <Button
                disabled={disabled}
                variant="quiet"
                aria-label={`Remove ${c.text}`}
                onClick={() => onChange(keys.filter((k) => k !== c.key))}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
      <SelectField<string>
        disabled={disabled}
        label="Add a tracker device"
        hint={
          choices.length === 0
            ? 'No tracking account has reported a device in the last 48 hours (Settings, Integrations).'
            : 'Every device your tracking accounts reported in the last 48 hours, and your tracker assets.'
        }
        value=""
        onChange={(v) => {
          if (v) onChange([...keys, v]);
        }}
        options={[
          ['', options.length === 0 ? 'No other devices' : 'Choose a device'],
          ...options.map(
            (o) =>
              [
                o.choice.key,
                o.choice.text +
                  (o.matches ? ' · Matches the registration' : '') +
                  (o.linkedTo ? ` · now on ${o.linkedTo}` : ''),
              ] as const,
          ),
        ]}
      />
    </fieldset>
  );
}
